import {
  CODIGO_PENDIENTE_CATALOGO,
  TIPOS_PENDIENTE,
  type Aviso,
  type MiAlcance,
  type PendienteCatalogo,
  type PendienteDeTienda,
  type TiendaDePendiente,
  type TipoPendiente,
  type UltimaSincronizacion,
} from '../types/metas.types';
import { momentoMx } from './formato';

/**
 * Pendientes de catálogo (fase 4): lo que la pantalla dice de ellos, sin React.
 *
 * Un pendiente es un dato de catálogo que falta o está mal en su sistema de
 * origen (BUK, el ERP, la fuente de metas de tienda): sin puesto, alta sin
 * clave de vendedor, puesto sin esquema de comisión, tienda sin meta de tienda
 * y colaborador en dos tiendas. NO se editan aquí: se corrigen en origen y la
 * siguiente sincronización los cierra sola. Qué tipos BLOQUEAN el envío y la
 * autorización lo dice la API (configurable); aquí sólo se muestra.
 */

/** Las etiquetas del diseño, de respaldo (la API manda las suyas en `tipos`). */
export const ETIQUETA_TIPO_PENDIENTE: Record<TipoPendiente, string> = {
  COLABORADOR_SIN_PUESTO: 'Colaborador sin puesto',
  ALTA_SIN_CLAVE_VENDEDOR: 'Alta sin clave de vendedor',
  PUESTO_SIN_ESQUEMA_COMISION: 'Puesto sin esquema de comisión',
  TIENDA_SIN_META_TIENDA: 'Tienda sin meta de tienda',
  COLABORADOR_EN_DOS_TIENDAS: 'Colaborador en dos tiendas',
};

export const esTipoPendiente = (v: unknown): v is TipoPendiente =>
  typeof v === 'string' && (TIPOS_PENDIENTE as string[]).includes(v);

/** La columna «Abierto»: «Hoy», «1 día», «5 días». */
export function textoAbierto(dias: number): string {
  const n = Number.isFinite(dias) && dias > 0 ? Math.floor(dias) : 0;
  if (n === 0) return 'Hoy';
  return n === 1 ? '1 día' : `${n} días`;
}

/**
 * «Última sincronización hoy 10:30» (hora de México). Si la última corrida
 * FALLÓ se dice —la lista es la de antes y puede estar vieja—; si sigue
 * corriendo, también.
 */
export function textoUltimaSincronizacion(
  u: UltimaSincronizacion | null,
  enCurso: boolean,
  ahora: number = Date.now(),
): { texto: string; fallo: boolean } {
  if (!u) return { texto: enCurso ? 'Sincronizando por primera vez…' : 'Todavía no se ha sincronizado', fallo: false };
  if (u.error)
    return {
      texto: `La última sincronización falló (${momentoMx(u.iniciadoEn, ahora)}): la lista puede no estar al día`,
      fallo: true,
    };
  // Sin terminar y sin nadie corriendo: murió a la mitad (la API se reinició).
  // Decir «Sincronizando…» hasta el siguiente cron era falso (F4-09).
  if (!u.terminadoEn && !enCurso)
    return {
      texto: `La última sincronización no terminó (empezó ${momentoMx(u.iniciadoEn, ahora)}): la lista puede no estar al día`,
      fallo: true,
    };
  if (!u.terminadoEn || enCurso) return { texto: `Sincronizando… (empezó ${momentoMx(u.iniciadoEn, ahora)})`, fallo: false };
  return { texto: `Última sincronización ${momentoMx(u.terminadoEn, ahora)}`, fallo: false };
}

/** Los pendientes de la tienda, por persona (`colaboradorKey`); los de la tienda no van. */
export function pendientesPorFila(ps: readonly PendienteDeTienda[] | null | undefined): Map<string, PendienteDeTienda[]> {
  const m = new Map<string, PendienteDeTienda[]>();
  for (const p of ps ?? []) {
    if (!p.colaboradorKey) continue;
    const l = m.get(p.colaboradorKey) ?? [];
    l.push(p);
    m.set(p.colaboradorKey, l);
  }
  return m;
}

/** Los que bloquean el envío (y la autorización) y los informativos, por separado. */
export function separarPendientes(ps: readonly PendienteDeTienda[] | null | undefined): {
  bloquean: PendienteDeTienda[];
  informan: PendienteDeTienda[];
} {
  const l = ps ?? [];
  return { bloquean: l.filter((p) => p.bloquea), informan: l.filter((p) => !p.bloquea) };
}

/** ¿El 422 es por pendientes de catálogo? */
export const esBloqueoCatalogo = (errores: readonly Aviso[] | null | undefined): boolean =>
  (errores ?? []).some((e) => e.codigo === CODIGO_PENDIENTE_CATALOGO);

/**
 * El texto que se copia con «Ver ficha»: el número de empleado (es por lo que
 * se busca en BUK), o el nombre si no hay número.
 */
export function textoFicha(p: Pick<PendienteCatalogo, 'numeroColaborador' | 'referencia'>): string {
  return p.numeroColaborador !== null && p.numeroColaborador !== undefined ? String(p.numeroColaborador) : p.referencia;
}

export const nombreTiendaPendiente = (t: TiendaDePendiente): string => `${t.chain} ${t.num}${t.tienda ? ` · ${t.tienda}` : ''}`;

/**
 * A qué tiendas lleva «Ver tienda»: la del pendiente, o (colaborador en dos
 * tiendas) cada una de las que la cuenta ve. Un administrador, todas.
 */
export function tiendasDestino(p: Pick<PendienteCatalogo, 'tienda' | 'tiendas'>, alcance: Pick<MiAlcance, 'esAdmin' | 'tiendas'>): TiendaDePendiente[] {
  const todas = p.tienda ? [p.tienda] : p.tiendas ?? [];
  if (alcance.esAdmin) return todas;
  const mias = new Set(alcance.tiendas.map((t) => `${t.chain}|${t.num}`));
  return todas.filter((t) => mias.has(`${t.chain}|${t.num}`));
}

/**
 * Cómo se cerró un pendiente: «Venció (semana cerrada)» si salió de la ventana
 * de semanas sin que nadie lo corrigiera; si no, «Resuelto» (F4-04).
 */
export function textoCierre(p: Pick<PendienteCatalogo, 'datos'>): { texto: string; vencio: boolean } {
  return p.datos?.cierre === 'semana-fuera-de-ventana'
    ? { texto: 'Venció (semana cerrada)', vencio: true }
    : { texto: 'Resuelto', vencio: false };
}

/** ¿El pendiente es de esa tienda (la suya, o una de las que toca)? */
export function esDeTienda(p: Pick<PendienteCatalogo, 'tienda' | 'tiendas'>, t: { chain: string; num: number }): boolean {
  if (p.tienda && p.tienda.chain === t.chain && Number(p.tienda.num) === Number(t.num)) return true;
  return (p.tiendas ?? []).some((x) => x.chain === t.chain && Number(x.num) === Number(t.num));
}

/**
 * El pie del panel «Ver afectados»: cuántos se muestran de cuántos. Al admin no
 * se le dice «de tus tiendas»: ve los de toda la cadena (F4-V1).
 */
export function textoMuestraAfectados(
  datos: PendienteCatalogo['datos'],
  esAdmin: boolean,
): string | null {
  const mostrados = datos?.personas?.length ?? 0;
  const total = typeof datos?.personasTotal === 'number' ? datos.personasTotal : null;
  if (total === null || mostrados >= total) return null;
  return esAdmin ? `Se muestran ${mostrados} de ${total}.` : `Se muestran ${mostrados} de ${total} de tus tiendas.`;
}

/** La etiqueta del botón de la columna «Acción». */
export const ETIQUETA_ACCION: Record<PendienteCatalogo['accion'], string> = {
  ver_ficha: 'Ver ficha',
  ver_afectados: 'Ver afectados',
  ver_tienda: 'Ver tienda',
};
